/**
 * 云服务类别 产品图表页 详情Dialog
 *
 * @format
 */

import React, { useState, useEffect } from 'react'
import { _$, Dialog, Button, Select, Option } from '@king-design/react'
import styled from 'styled-components'
import QuickRangeDate, { EDIT_GRAPH_CHART_QUICK_RANGE } from 'src/components/QuickRangeDate'
import getMetricStatisticsBatch from './getMetricStatisticsBatch'
import { IMonitorGraphData, calcSelectInterval, delayToHours } from './constants'
import { tooltipFormatter, handleUnit, unitFormatBps, unitFormatbps } from 'src/utils'
import NoData from 'src/components/NoData'
import { EchartReact } from 'src/components'
import { echartTooltipOptions } from 'src/utils/echarts'

interface MonitorChartDetailsProps {
  visible: boolean
  start: number
  end: number
  graph: IMonitorGraphData
  closeExport: () => void
  className?: string
}

const MonitorChartDetails: React.FC<MonitorChartDetailsProps> = ({
  visible,
  start,
  end,
  graph,
  closeExport,
  className
}) => {
  const [dateTime, setDateTime] = useState<[number, number]>([null, null])
  const [delayOption, setDelayOption] = useState<number[]>([])
  const [delay, setDelay] = useState<number>()

  // dashboard query 接口
  const { getDashboardChart, chartData } = getMetricStatisticsBatch()
  // 初始化
  useEffect(() => {
    if (visible) {
      const delayArray = calcSelectInterval(start, end, [graph])
      setDelayOption(delayArray)
      setDelay(delayArray[0])
      setDateTime([start, end])
      getDashboardChart(start, end, delayArray[0], graph)
    }
  }, [visible])

  const handleDateTimeChange = date => {
    setDateTime(date)
    if (date[0] && date[1]) {
      const delayArray = calcSelectInterval(date[0], date[1], [graph])
      setDelayOption(delayArray)
      setDelay(delayArray[0])
      getDashboardChart(date[0], date[1], delayArray[0], graph)
    }
  }

  const handleDelayChange = value => {
    setDelay(value)
    getDashboardChart(dateTime[0], dateTime[1], value, graph)
  }

  const { unit } = React.useMemo(() => {
    if (chartData?.series?.length) {
      // 计算图表最大值最小值
      // const values: number[] = []
      // chartData?.series?.forEach(item => {
      //   const data = item.data.map(value => Number(value))
      //   values.push(...data)
      // })
      // const max = maxBy(values) as number
      // const min = minBy(values) as number
      const first = chartData.series[0].name.split('===')[1]
      if (unitFormatBps(first || '') || unitFormatbps(first || '')) {
        const bpsUnit = chartData.series.every(item => {
          const titleSplit = item?.name.split('===')[1]
          return titleSplit === first
        })
        return {
          unit: bpsUnit ? first : ''
          // yMax: max,
          // yMin: min
        }
      } else {
        return {
          unit: ''
          // yMax: max,
          // yMin: min
        }
      }
    } else {
      return {
        unit: ''
      }
    }
  }, [chartData])

  return (
    <Dialog
      className={className}
      mode="destroy"
      width={1096}
      value={visible}
      onChangeValue={show => {
        !show && closeExport()
      }}
      title={graph?.title || _$('图表详情')}
      slotFooter={
        <React.Fragment>
          <Button onClick={closeExport}>{_$('关闭')}</Button>
        </React.Fragment>
      }
    >
      <div>
        <div className="date-select">
          <QuickRangeDate
            realoadIcon={false}
            format="YYYY-MM-DD HH:mm"
            optionalDateRange={90}
            dateDiff={31}
            defautlValue={dateTime}
            quickRange={EDIT_GRAPH_CHART_QUICK_RANGE}
            changeValue={handleDateTimeChange}
          />
          <div className="delay-select">
            <span>{_$('周期')}：</span>
            <Select value={delay} onChangeValue={handleDelayChange} width={120}>
              {delayOption.length
                ? delayOption.map(item => (
                    <Option key={item} value={item}>
                      {delayToHours(item)}
                    </Option>
                  ))
                : null}
            </Select>
          </div>
        </div>
        <div className="chart-box">
          {chartData ? (
            <EchartReact
              style={{ width: '100%', height: '100%' }}
              option={{
                xAxis: {
                  type: 'category',
                  boundaryGap: false,
                  data: chartData?.xData || [],
                  splitLine: {
                    show: true
                  }
                },
                series: chartData?.series || [],
                yAxis: {
                  name: `${_$('周期')}:${chartData?.interval}s`,
                  type: 'value',
                  splitLine: {
                    show: true,
                    lineStyle: {
                      type: 'dashed',
                      color: '#d8dae2'
                    }
                  },
                  axisLabel: {
                    formatter: function (value) {
                      return handleUnit(value, unit, true)
                    }
                  }
                },
                tooltip: {
                  ...echartTooltipOptions,
                  axisPointer: {
                    type: 'cross',
                    animation: false,
                    label: {
                      formatter: function (params) {
                        return params.axisDimension === 'y'
                          ? handleUnit(params.value, unit, true)
                          : params.value
                      }
                    }
                  },
                  formatter: function (params) {
                    return tooltipFormatter(params)
                  }
                },
                legend: {
                  type: 'scroll',
                  right: '0',
                  width: '80%',
                  top: '-3px',
                  formatter: name => {
                    const nameSplit = name.split('===')
                    return nameSplit[0]
                  }
                },
                dataZoom:
                  chartData?.xData?.length > 20
                    ? [
                        {
                          type: 'inside',
                          moveOnMouseMove: true
                        },
                        {
                          type: 'slider', // 滑动条缩放
                          xAxisIndex: [0, 1],
                          showDetail: false,
                          height: 20, // 固定高度
                          bottom: 10, // 距离底部10px
                          handleStyle: {
                            color: '#DEE5F8'
                          }
                        }
                      ]
                    : [],
                grid: {
                  top: 35,
                  left: 30,
                  right: 30,
                  bottom: 40,
                  containLabel: true
                }
              }}
            />
          ) : (
            <NoData />
          )}
        </div>
      </div>
    </Dialog>
  )
}

export default styled(MonitorChartDetails)`
  .date-select {
    display: flex;
    .delay-select {
      margin-left: 16px;
    }
  }
  .chart-box {
    margin-top: 16px;
    width: 100%;
    height: 350px;
  }
`
